/**
 * Admin 콘솔 전용 경량 i18n (ko/en).
 *
 * 사용자 앱은 6-locale 글로벌 TKey 시스템을 쓰지만, admin은 **운영자 내부 도구**라
 * 전 6개 언어 패리티의 ROI가 낮다 → ko/en 2개만. 현재 locale이 'ko'면 한국어, 그 외 영어.
 * (설계서 §9 deviation 기록.) 전체 6-locale 확장은 Phase 2+에서 필요 시.
 */
import { useTranslation } from '../../i18n';

type Lang = 'ko' | 'en';

const STR: Record<Lang, Record<string, string>> = {
  ko: {
    'console': '관리자 콘솔',
    'backToApp': '앱으로',
    'refresh': '새로고침',
    'loading': '불러오는 중…',
    'emptyGeneric': '항목이 없습니다.',
    'allClear': '처리할 항목이 없습니다 — 깨끗합니다.',

    // nav
    'nav.overview': '개요',
    'nav.content': '콘텐츠',
    'nav.review': '검토 큐',
    'nav.proposals': '제안 큐',
    'nav.curation': '큐레이션',
    'nav.ops': '운영',
    'nav.jobs': '작업 모니터',
    'nav.cost': '비용',
    'nav.opsReport': '운영 리포트',
    'nav.reports': '신고',
    'nav.policy': '정책',
    'nav.pricing': '가격',
    'nav.people': '사용자',
    'nav.credentials': '크리덴셜',
    'nav.gov': '거버넌스',
    'nav.institutions': '기관',
    'nav.gamification': '게임화 감사',
    'nav.certProfiles': '인증 프로파일',
    'nav.standards': '표준',

    // jobs
    'jobs.title': '작업 모니터',
    'jobs.subtitle': '분석 파이프라인 상태 (읽기 전용)',
    'jobs.selectHint': '작업을 선택해 상세를 보세요.',
    'jobs.error': '실패 원인',
    'jobs.degradations': '부분산출(열화)',
    'jobs.cost': '비용',
    'jobs.calls': '콜 수',
    'jobs.stage': '단계',
    'jobs.none': '작업이 없습니다.',
    // cost
    'cost.title': '비용 대시보드',
    'cost.subtitle': 'LLM 비용 원장 롤업',
    'cost.total': '총 비용',
    'cost.calls': '총 콜',
    'cost.byModel': '모델별',
    'cost.byDay': '일별 추이',
    'cost.range7': '7일',
    'cost.range30': '30일',
    'cost.range90': '90일',
    'ops.title': '월별 운영 리포트',
    'ops.subtitle': '작업·비용 월별 추이',
    'ops.month': '월',
    'ops.jobsTotal': '작업',
    'ops.succeeded': '성공',
    'ops.failed': '실패',
    'ops.cost': '비용',
    // reports
    'reports.title': '신고 처리',
    'reports.subtitle': '사용자 문항 신고 큐',
    'reports.resolve': '해결',
    'reports.dismiss': '기각',
    'reports.reason': '사유',
    'reports.detail': '상세',
    'reports.resolved': '처리했습니다.',
    'reports.none': '신고가 없습니다.',
    // institutions
    'inst.title': '기관 / 멀티테넌시',
    'inst.subtitle': '라이선스·시트·LTI 연동',
    'inst.seats': '시트',
    'inst.license': '라이선스',
    'inst.lti': 'LTI',
    'inst.suspend': '정지',
    'inst.activate': '활성화',
    'inst.archive': '보관',
    'inst.changed': '상태를 변경했습니다.',
    // gamification audit
    'gaudit.title': '게임화 감사',
    'gaudit.subtitle': 'XP 이상치·뱃지 분포',
    'gaudit.totalXp': '총 XP',
    'gaudit.events': '이벤트',
    'gaudit.byKind': '이벤트 종류별',
    'gaudit.topUsers': '상위 사용자(스팸 의심)',
    'gaudit.badges': '뱃지 분포',
    'gaudit.user': '사용자',
    // cert profiles
    'cprof.title': '인증 프로파일',
    'cprof.subtitle': '발급 규칙(5-tier ladder)',
    'cprof.active': '활성',
    'cprof.default': '기본',
    'cprof.system': '시스템',
    'cprof.toggled': '활성 상태를 변경했습니다.',
    // standards
    'std.title': '표준 매핑',
    'std.subtitle': 'CCSS/NAEP/PIRLS 등 평가 표준 (읽기 전용)',
    'std.primary': '주 표준',
    'std.report': '리포트 형식',

    // actions
    'action.approve': '승인',
    'action.reject': '거부',
    'action.revise': '수정요청',
    'action.accept': '채택',
    'action.decline': '거부',
    'action.revoke': '취소',
    'action.remove': '제거',
    'action.save': '저장',
    'action.cancel': '취소',
    'action.add': '추가',
    'action.edit': '편집',
    'reason': '사유',
    'notes': '메모 (선택)',
    'confirm': '확인',

    // overview
    'overview.title': '개요',
    'overview.subtitle': '오늘의 운영 현황',
    'stat.reviewPending': '검토 대기',
    'stat.proposalPending': '제안 대기',
    'stat.libraryBooks': '라이브러리 책',
    'stat.paidUploads': '유료 업로드',
    'stat.userUploads': '전체 업로드',

    // review
    'review.title': '검토 큐 (LARC)',
    'review.subtitle': 'larc_pending 문항 판정',
    'review.selectHint': '왼쪽에서 문항을 선택해 판정하세요.',
    'review.confidence': '신뢰도',
    'review.answer': '정답',
    'review.evidence': '근거',
    'review.done': '판정 완료',

    // proposals
    'proposals.title': '제안 큐',
    'proposals.subtitle': '사용자 제안 채택/거부',
    'proposals.selectHint': '왼쪽에서 제안을 선택하세요.',
    'proposals.proposer': '제안자',
    'proposals.adopted': '채택됨 — 문제은행 편입(검토 대기)',

    // curation
    'curation.title': '라이브러리 큐레이션',
    'curation.formTitle': '책 추가',
    'curation.bookId': '책 ID',
    'curation.category': '카테고리',
    'curation.gradeBand': '학년대',
    'curation.sortWeight': '정렬 가중치',
    'curation.tags': '태그 (쉼표 구분)',
    'curation.featured': '추천(featured)',
    'curation.rationale': '선정 이유',
    'curation.listTitle': '큐레이션 목록',
    'curation.removeConfirm': '이 책을 라이브러리에서 제거할까요? (비공개로 전환)',
    'curation.added': '큐레이션에 추가했습니다.',
    'curation.removed': '제거했습니다.',

    // pricing
    'pricing.title': '가격 정책',
    'pricing.editTitle': '정책 편집',
    'pricing.uploadPrice': '업로드 가격(USD)',
    'pricing.sharePrice': '공유 가격(USD)',
    'pricing.libraryPrice': '라이브러리 가격(USD)',
    'pricing.active': '활성',
    'pricing.default': '기본',
    'pricing.saved': '정책을 저장했습니다.',

    // credentials
    'credentials.title': '크리덴셜',
    'credentials.subtitle': '발급 조회 + 취소',
    'credentials.revokeTitle': '크리덴셜 취소',
    'credentials.revokeConfirm': '이 크리덴셜을 취소합니다. 되돌릴 수 없습니다.',
    'credentials.revoked': '취소 요청을 접수했습니다.',
    'credentials.allTiers': '전체 등급',

    // columns
    'col.code': '코드',
    'col.tier': 'Tier',
    'col.category': '유형',
    'col.stem': '문제',
    'col.status': '상태',
    'col.confidence': '신뢰도',
    'col.book': '책',
    'col.format': '형식',
    'col.proposer': '제안자',
    'col.created': '생성',
    'col.urn': 'URN',
    'col.type': '유형',
    'col.issued': '발급일',
    'col.user': '사용자',
    'col.featured': '추천',
    'col.weight': '가중치',
    'col.actions': '액션',
    'col.name': '이름',
    'col.upload': '업로드',
  },
  en: {
    'console': 'Admin Console',
    'backToApp': 'Back to app',
    'refresh': 'Refresh',
    'loading': 'Loading…',
    'emptyGeneric': 'No items.',
    'allClear': 'Nothing to do — all clear.',

    'nav.overview': 'Overview',
    'nav.content': 'Content',
    'nav.review': 'Review queue',
    'nav.proposals': 'Proposals',
    'nav.curation': 'Curation',
    'nav.ops': 'Operations',
    'nav.jobs': 'Jobs',
    'nav.cost': 'Cost',
    'nav.opsReport': 'Ops report',
    'nav.reports': 'Reports',
    'nav.policy': 'Policy',
    'nav.pricing': 'Pricing',
    'nav.people': 'People',
    'nav.credentials': 'Credentials',
    'nav.gov': 'Governance',
    'nav.institutions': 'Institutions',
    'nav.gamification': 'Gamification audit',
    'nav.certProfiles': 'Cert profiles',
    'nav.standards': 'Standards',

    'jobs.title': 'Jobs monitor',
    'jobs.subtitle': 'Analysis pipeline status (read-only)',
    'jobs.selectHint': 'Select a job to see details.',
    'jobs.error': 'Failure cause',
    'jobs.degradations': 'Degradations',
    'jobs.cost': 'Cost',
    'jobs.calls': 'Calls',
    'jobs.stage': 'Stage',
    'jobs.none': 'No jobs.',
    'cost.title': 'Cost dashboard',
    'cost.subtitle': 'LLM cost ledger rollup',
    'cost.total': 'Total cost',
    'cost.calls': 'Total calls',
    'cost.byModel': 'By model',
    'cost.byDay': 'Daily trend',
    'cost.range7': '7d',
    'cost.range30': '30d',
    'cost.range90': '90d',
    'ops.title': 'Monthly ops report',
    'ops.subtitle': 'Jobs & cost by month',
    'ops.month': 'Month',
    'ops.jobsTotal': 'Jobs',
    'ops.succeeded': 'Succeeded',
    'ops.failed': 'Failed',
    'ops.cost': 'Cost',
    'reports.title': 'Reports',
    'reports.subtitle': 'User question report queue',
    'reports.resolve': 'Resolve',
    'reports.dismiss': 'Dismiss',
    'reports.reason': 'Reason',
    'reports.detail': 'Detail',
    'reports.resolved': 'Done.',
    'reports.none': 'No reports.',

    'action.approve': 'Approve',
    'action.reject': 'Reject',
    'action.revise': 'Request revision',
    'action.accept': 'Adopt',
    'action.decline': 'Decline',
    'action.revoke': 'Revoke',
    'action.remove': 'Remove',
    'action.save': 'Save',
    'action.cancel': 'Cancel',
    'action.add': 'Add',
    'action.edit': 'Edit',
    'reason': 'Reason',
    'notes': 'Notes (optional)',
    'confirm': 'Confirm',

    'overview.title': 'Overview',
    'overview.subtitle': 'Today’s operations',
    'stat.reviewPending': 'Review pending',
    'stat.proposalPending': 'Proposals pending',
    'stat.libraryBooks': 'Library books',
    'stat.paidUploads': 'Paid uploads',
    'stat.userUploads': 'Total uploads',

    'review.title': 'Review queue (LARC)',
    'review.subtitle': 'Adjudicate larc_pending items',
    'review.selectHint': 'Select an item on the left to adjudicate.',
    'review.confidence': 'Confidence',
    'review.answer': 'Answer',
    'review.evidence': 'Evidence',
    'review.done': 'Decision recorded',

    'proposals.title': 'Proposals',
    'proposals.subtitle': 'Adopt or decline user proposals',
    'proposals.selectHint': 'Select a proposal on the left.',
    'proposals.proposer': 'Proposer',
    'proposals.adopted': 'Adopted — added to bank (pending review)',

    'curation.title': 'Library curation',
    'curation.formTitle': 'Add a book',
    'curation.bookId': 'Book ID',
    'curation.category': 'Category',
    'curation.gradeBand': 'Grade band',
    'curation.sortWeight': 'Sort weight',
    'curation.tags': 'Tags (comma-separated)',
    'curation.featured': 'Featured',
    'curation.rationale': 'Rationale',
    'curation.listTitle': 'Curated list',
    'curation.removeConfirm': 'Remove this book from the library? (set to private)',
    'curation.added': 'Added to curation.',
    'curation.removed': 'Removed.',

    'pricing.title': 'Pricing policies',
    'pricing.editTitle': 'Edit policy',
    'pricing.uploadPrice': 'Upload price (USD)',
    'pricing.sharePrice': 'Share price (USD)',
    'pricing.libraryPrice': 'Library price (USD)',
    'pricing.active': 'Active',
    'pricing.default': 'Default',
    'pricing.saved': 'Policy saved.',

    'credentials.title': 'Credentials',
    'credentials.subtitle': 'Browse + revoke',
    'credentials.revokeTitle': 'Revoke credential',
    'credentials.revokeConfirm': 'This revokes the credential. It cannot be undone.',
    'credentials.revoked': 'Revocation recorded.',
    'credentials.allTiers': 'All tiers',

    'col.code': 'Code',
    'col.tier': 'Tier',
    'col.category': 'Type',
    'col.stem': 'Question',
    'col.status': 'Status',
    'col.confidence': 'Conf.',
    'col.book': 'Book',
    'col.format': 'Format',
    'col.proposer': 'Proposer',
    'col.created': 'Created',
    'col.urn': 'URN',
    'col.type': 'Type',
    'col.issued': 'Issued',
    'col.user': 'User',
    'col.featured': 'Featured',
    'col.weight': 'Weight',
    'col.actions': 'Actions',
    'col.name': 'Name',
    'col.upload': 'Upload',
    'inst.title': 'Institutions',
    'inst.subtitle': 'Licenses · seats · LTI',
    'inst.seats': 'Seats',
    'inst.license': 'License',
    'inst.lti': 'LTI',
    'inst.suspend': 'Suspend',
    'inst.activate': 'Activate',
    'inst.archive': 'Archive',
    'inst.changed': 'Status updated.',
    'gaudit.title': 'Gamification audit',
    'gaudit.subtitle': 'XP anomalies · badge distribution',
    'gaudit.totalXp': 'Total XP',
    'gaudit.events': 'Events',
    'gaudit.byKind': 'By event kind',
    'gaudit.topUsers': 'Top users (spam suspects)',
    'gaudit.badges': 'Badge distribution',
    'gaudit.user': 'User',
    'cprof.title': 'Cert profiles',
    'cprof.subtitle': 'Issuance rules (5-tier ladder)',
    'cprof.active': 'Active',
    'cprof.default': 'Default',
    'cprof.system': 'System',
    'cprof.toggled': 'Active state updated.',
    'std.title': 'Standards mapping',
    'std.subtitle': 'CCSS/NAEP/PIRLS assessment standards (read-only)',
    'std.primary': 'Primary',
    'std.report': 'Report format',
  },
};

export type AdminTKey = keyof (typeof STR)['ko'];

export function useAdminT() {
  const { locale } = useTranslation();
  const lang: Lang = locale === 'ko' ? 'ko' : 'en';
  return (key: AdminTKey): string => STR[lang][key] ?? STR.en[key] ?? key;
}
